import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../assets/variables.css';
import '../assets/clients.css';
import '../assets/panels.css';
import Loader from '../components/Loader';

const ClientEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Initial state matching Laravel form structure
  const [client, setClient] = useState({
    name: '',
    status_id: '',
    manager_id: '',
    lead_source_id: '',
    market_segment_id: '',
    bank_account_id: '',
    bank_details_account_name: '',
    bank_details_bank_name: '',
    bank_details_sort_code: '',
    bank_details_number: '',
    logo: '', // Set to empty string by default
    logoKey: null, // S3 key for uploaded logo
    web: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    phone_number: '',
    email: '',
    fax_number: '',
    internal_info: '',
    region: '',
    vip_amount: 0,
    savings_made: 0,
    send_immediately_email_to_guardians_ntq: true
  });

  const [dropdowns, setDropdowns] = useState({
    statuses: [],
    accountManagers: [],
    leadSources: [],
    marketSegments: [],
    bankAccounts: [],
    clientContacts: [],
    regions: ['London & South East', 'Bristol & South West', 'Birmingham & Midlands', 'Manchester & North', 'Ireland', 'France', 'America', 'Asia', 'Europe'] // Fallback regions (matching Laravel config)
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    // Fetch client edit data
    fetch(`${process.env.REACT_APP_API_URL}/api/clients/${id}/edit`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => res.json())
      .then(data => {
        // Map API response to form state structure
        const apiClient = data.client;

        const mappedClient = {
          name: apiClient.name || '',
          status_id: apiClient.status_id || '',
          manager_id: apiClient.manager_id || '',
          lead_source_id: apiClient.lead_source_id || '',
          market_segment_id: apiClient.market_segment_id || '',
          bank_account_id: apiClient.bank_account_id || '',
          bank_details_account_name: apiClient.bank_details_account_name || '',
          bank_details_bank_name: apiClient.bank_details_bank_name || '',
          bank_details_sort_code: apiClient.bank_details_sort_code || '',
          bank_details_number: apiClient.bank_details_number || '',
          logo: apiClient.logo || '',
          web: apiClient.web || '',
          address_1: apiClient.address_1 || '',
          address_2: apiClient.address_2 || '',
          address_3: apiClient.address_3 || '',
          town: apiClient.town || '',
          postcode: apiClient.postcode || '',
          phone_number: apiClient.phone_number || '',
          email: apiClient.email || '',
          fax_number: apiClient.fax_number || '',
          internal_info: apiClient.internal_info || '',
          region: apiClient.region || '',
          vip_amount: apiClient.vip_amount || 0,
          savings_made: apiClient.savings_made || 0,
          send_immediately_email_to_guardians_ntq: apiClient.send_immediately_email_to_guardians_ntq !== false
        };

        setClient(mappedClient);
        setLogoPreview(apiClient.logo || null);
        setDropdowns({
          statuses: data.statuses || [],
          accountManagers: data.accountManagers || [],
          leadSources: data.leadSources || [],
          marketSegments: data.marketSegments || [],
          bankAccounts: data.bankAccounts || [],
          clientContacts: data.clientContacts || [],
          regions: data.regions || ['London & South East', 'Bristol & South West', 'Birmingham & Midlands', 'Manchester & North', 'Ireland', 'France', 'America', 'Asia', 'Europe'], // Use API regions or fallback (matching Laravel config)
        });
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load client data');
        setLoading(false);
      });
  }, [id]);

  // Helper for dot notation in handleChange
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setClient(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // S3 upload helper with fallback (presigned URL -> direct upload)
  async function uploadToS3(file) {
    try {
      // Method 1: Try presigned URL first (faster, direct to S3)
      console.log('Attempting presigned URL upload...');
      const presignRes = await fetch(`${process.env.REACT_APP_API_URL}/api/s3/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ 
          fileName: file.name, 
          fileType: file.type,
          folder: 'uploads/images/clients' // Match Laravel config('client.images')
        }),
      });

      if (presignRes.ok) {
        const { url, key } = await presignRes.json();
        // Upload file to S3 using presigned URL
        const uploadRes = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        if (uploadRes.ok) {
          console.log('Presigned URL upload successful');
          return key;
        }
      }

      throw new Error('Presigned URL upload failed, trying fallback...');

    } catch (presignError) {
      console.warn('Presigned URL upload failed:', presignError);

      try {
        // Method 2: Fallback to direct upload (slower, goes through backend)
        console.log('Attempting direct upload fallback...');
        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await fetch(`${process.env.REACT_APP_API_URL}/api/s3/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (uploadRes.ok) {
          const { key } = await uploadRes.json();
          console.log('Direct upload fallback successful');
          return key;
        } else {
          const errorData = await uploadRes.json();
          throw new Error(errorData.error || 'Direct upload failed');
        }

      } catch (directError) {
        console.error('Both upload methods failed:', directError);
        throw new Error(`Upload failed: ${directError.message}`);
      }
    }
  }

  const handleLogoChange = async e => {
    const file = e.target.files[0];
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
      setSaving(true);
      try {
        const logoKey = await uploadToS3(file);
        setClient(prev => ({
          ...prev,
          logo: file, // Keep file for preview
          logoKey: logoKey // Store S3 key for backend
        }));
        setError(null); // Clear any previous errors
      } catch (err) {
        console.warn('Logo upload failed, continuing with empty logo field:', err);
        setClient(prev => ({
          ...prev,
          logo: file, // Keep file for preview
          logoKey: null // Clear S3 key, treat as empty
        }));
        // Don't set error - let the form continue with empty logo
      }
      setSaving(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required fields validation
    if (!client.name) newErrors.name = 'Name is required';
    if (!client.status_id) newErrors.status_id = 'Status is required';
    if (!client.bank_account_id) newErrors.bank_account_id = 'Bank account is required';

    // Field length validations
    if (client.name && client.name.length > 255) newErrors.name = 'Name must not exceed 255 characters';
    if (client.email && client.email.length > 100) newErrors.email = 'Email must not exceed 100 characters';
    if (client.bank_details_account_name && client.bank_details_account_name.length > 255) {
      newErrors.bank_details_account_name = 'Bank account name must not exceed 255 characters';
    }
    if (client.bank_details_bank_name && client.bank_details_bank_name.length > 255) {
      newErrors.bank_details_bank_name = 'Bank name must not exceed 255 characters';
    }
    if (client.bank_details_sort_code && client.bank_details_sort_code.length > 255) {
      newErrors.bank_details_sort_code = 'Sort code must not exceed 255 characters';
    }
    if (client.address_1 && client.address_1.length > 255) newErrors.address_1 = 'Address 1 must not exceed 255 characters';
    if (client.address_2 && client.address_2.length > 255) newErrors.address_2 = 'Address 2 must not exceed 255 characters';
    if (client.address_3 && client.address_3.length > 255) newErrors.address_3 = 'Address 3 must not exceed 255 characters';
    if (client.town && client.town.length > 255) newErrors.town = 'Town must not exceed 255 characters';
    if (client.postcode && client.postcode.length > 255) newErrors.postcode = 'Postcode must not exceed 255 characters';
    if (client.phone_number && client.phone_number.length > 255) newErrors.phone_number = 'Phone number must not exceed 255 characters';
    if (client.fax_number && client.fax_number.length > 255) newErrors.fax_number = 'Fax number must not exceed 255 characters';
    if (client.web && client.web.length > 255) newErrors.web = 'Website must not exceed 255 characters';

    // Email validation
    if (client.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) {
      newErrors.email = 'Email must be a valid email address';
    }

    // Bank account number validation
    if (client.bank_details_number && !/^\d+$/.test(client.bank_details_number)) {
      newErrors.bank_details_number = 'Bank account number must be numeric';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);

    // Prepare data for submission
    const submitData = {
      name: client.name,
      status_id: client.status_id,
      bank_account_id: client.bank_account_id,
      manager_id: client.manager_id || null,
      lead_source_id: client.lead_source_id || null,
      market_segment_id: client.market_segment_id || null,
      bank_details_account_name: client.bank_details_account_name || null,
      bank_details_bank_name: client.bank_details_bank_name || null,
      bank_details_sort_code: client.bank_details_sort_code || null,
      bank_details_number: client.bank_details_number || null,
      logo: client.logoKey || client.logo || '',
      web: client.web || '',
      address_1: client.address_1 || '',
      address_2: client.address_2 || '',
      address_3: client.address_3 || '',
      town: client.town || '',
      postcode: client.postcode || '',
      phone_number: client.phone_number || '',
      email: client.email || null,
      fax_number: client.fax_number || '',
      internal_info: client.internal_info || '',
      region: client.region || null,
      vip_amount: client.vip_amount || 0,
      savings_made: client.savings_made || 0,
      send_immediately_email_to_guardians_ntq: client.send_immediately_email_to_guardians_ntq
    };

    fetch(`${process.env.REACT_APP_API_URL}/api/clients/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(submitData),
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(errData => {
            throw new Error(errData.message || 'Failed to update client');
          });
        }
        return res.json();
      })
      .then(data => {
        setSuccess(true);
        setSaving(false);
        // Navigate to client detail page
        setTimeout(() => {
          navigate(`/clients/${id}`);
        }, 1000);
      })
      .catch((error) => {
        setError(error.message || 'Failed to update client');
        setSaving(false);
      });
  };

  if (loading) return <Loader />;
  // if (error) return <div>{error}</div>;
  // if (!client) return <div>Client not found</div>;

  return (
    <div className="container js-ClientFormController" style={{ maxWidth: 900, margin: '0 auto', fontFamily: 'var(--font-roboto), Arial, sans-serif', padding: 32, borderRadius: 8 }}>
      <div className="breadcrumb-row mb-4">
        <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-clients" onClick={() => navigate('/clients')} style={{ cursor: 'pointer', color: '#337ab7' }}>Clients</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-clients" style={{ background: '#8bc34a', color: '#fff', padding: '2px 8px', borderRadius: 3 }}>Edit client</span>
      </div>

      <div className="row">
        <div className="col-xs-12">
          <div className="panel panel-default panel-form" style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', padding: 0 }}>
            <div className="panel-heading panel-title">
              <h3 style={{ fontWeight: 600, fontFamily: 'var(--font-stag), serif', fontSize: '29px', margin: 0, lineHeight: '35px', letterSpacing: '-0.435px' }}>Edit Client</h3>
            </div>

            <div className="panel-body" style={{ padding: 32 }}>
              <form className="form-horizontal" onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
                <fieldset className="form-horizontal">
                  {/* Name - required */}
                  <div className={`form-group mb-3${errors.name ? ' has-error' : ''}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="name" className="control-label col-sm-3 label-required" style={{ textAlign: 'right' }}>Name</label>
                    <div className="col-sm-5">
                      <input id="name" name="name" value={client.name} onChange={handleChange} className="form-control" placeholder="Enter client name" required />
                      {errors.name && <span className="help-block text-danger">{errors.name}</span>}
                    </div>
                  </div>

                  {/* Status - required */}
                  <div className={`form-group mb-3${errors.status_id ? ' has-error' : ''}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="status_id" className="control-label col-sm-3 label-required" style={{ textAlign: 'right' }}>Status</label>
                    <div className="col-sm-5">
                      <select id="status_id" name="status_id" value={client.status_id} onChange={handleChange} className="form-control" required>
                        <option value="">Please Select</option>
                        {dropdowns.statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      {errors.status_id && <span className="help-block text-danger">{errors.status_id}</span>}
                    </div>
                  </div>

                  {/* Manager */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="manager_id" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Account Manager</label>
                    <div className="col-sm-5">
                      <select id="manager_id" name="manager_id" value={client.manager_id} onChange={handleChange} className="form-control">
                        <option value="">Please Select</option>
                        {dropdowns.accountManagers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Lead Source */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="lead_source_id" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Lead Source</label>
                    <div className="col-sm-5">
                      <select id="lead_source_id" name="lead_source_id" value={client.lead_source_id} onChange={handleChange} className="form-control">
                        <option value="">Please Select</option>
                        {dropdowns.leadSources.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Market Segment */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="market_segment_id" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Market Segment</label>
                    <div className="col-sm-5">
                      <select id="market_segment_id" name="market_segment_id" value={client.market_segment_id} onChange={handleChange} className="form-control">
                        <option value="">Please Select</option>
                        {dropdowns.marketSegments.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Bank Account - required */}
                  <div className={`form-group mb-3${errors.bank_account_id ? ' has-error' : ''}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="bank_account_id" className="control-label col-sm-3 label-required" style={{ textAlign: 'right' }}>GG Bank Account</label>
                    <div className="col-sm-5">
                      <select id="bank_account_id" name="bank_account_id" value={client.bank_account_id} onChange={handleChange} className="form-control" required>
                        <option value="">Please Select</option>
                        {dropdowns.bankAccounts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                      {errors.bank_account_id && <span className="help-block text-danger">{errors.bank_account_id}</span>}
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="bank_details_account_name" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Bank Details</label>
                    <div className="col-sm-5">
                      <input id="bank_details_account_name" name="bank_details_account_name" value={client.bank_details_account_name} onChange={handleChange} className="form-control" placeholder="Account Name" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label className="control-label col-sm-3" style={{ textAlign: 'right' }}></label>
                    <div className="col-sm-5">
                      <input id="bank_details_bank_name" name="bank_details_bank_name" value={client.bank_details_bank_name} onChange={handleChange} className="form-control" placeholder="Bank Name" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label className="control-label col-sm-3" style={{ textAlign: 'right' }}></label>
                    <div className="col-sm-5">
                      <input id="bank_details_sort_code" name="bank_details_sort_code" value={client.bank_details_sort_code} onChange={handleChange} className="form-control" placeholder="Sort Code" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label className="control-label col-sm-3" style={{ textAlign: 'right' }}></label>
                    <div className="col-sm-5">
                      <input id="bank_details_number" name="bank_details_number" value={client.bank_details_number} onChange={handleChange} className="form-control" placeholder="Account Number" />
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="logo" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Logo</label>
                    <div className="col-sm-5">
                      <input id="logo" name="logo" type="file" className="form-control" ref={fileInputRef} onChange={handleLogoChange} accept="image/*" />
                    </div>
                  </div>

                  {/* Logo preview */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 15 }}>
                    <label className="control-label col-sm-3" style={{ textAlign: 'right', paddingTop: '8px' }}>Logo Preview</label>
                    <div className="col-sm-5">
                      {logoPreview ? (
                        <img src={logoPreview} className="img-responsive mb-20" alt="Client Logo" style={{ maxHeight: '300px' }} />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span>The example:</span>
                          <img src="/images/photograph-woman.jpg" className="img-responsive mb-20" alt="Example" style={{ maxHeight: '300px' }} />
                        </div>
                      )}
                    </div>
                  </div>



                  <div className="clearfix"></div>

                  {/* Website */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="web" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Website</label>
                    <div className="col-sm-5">
                      <input id="web" name="web" value={client.web} onChange={handleChange} className="form-control" placeholder="Enter website URL" />
                    </div>
                  </div>

                  {/* Address fields */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="address_1" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Address 1</label>
                    <div className="col-sm-5">
                      <input id="address_1" name="address_1" value={client.address_1} onChange={handleChange} className="form-control" placeholder="Enter address line 1" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="address_2" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Address 2</label>
                    <div className="col-sm-5">
                      <input id="address_2" name="address_2" value={client.address_2} onChange={handleChange} className="form-control" placeholder="Enter address line 2" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="address_3" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Address 3</label>
                    <div className="col-sm-5">
                      <input id="address_3" name="address_3" value={client.address_3} onChange={handleChange} className="form-control" placeholder="Enter address line 3" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="town" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Town</label>
                    <div className="col-sm-5">
                      <input id="town" name="town" value={client.town} onChange={handleChange} className="form-control" placeholder="Enter town/city" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="postcode" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Postcode</label>
                    <div className="col-sm-5">
                      <input id="postcode" name="postcode" value={client.postcode} onChange={handleChange} className="form-control" placeholder="Enter postcode" />
                    </div>
                  </div>

                  {/* Contact fields */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="phone_number" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Phone Number</label>
                    <div className="col-sm-5">
                      <input id="phone_number" name="phone_number" value={client.phone_number} onChange={handleChange} className="form-control" placeholder="Enter phone number" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="email" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Email</label>
                    <div className="col-sm-5">
                      <input id="email" name="email" type="email" value={client.email} onChange={handleChange} className="form-control" placeholder="Enter email address" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="fax_number" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Fax Number</label>
                    <div className="col-sm-5">
                      <input id="fax_number" name="fax_number" value={client.fax_number} onChange={handleChange} className="form-control" placeholder="Enter fax number" />
                    </div>
                  </div>

                  {/* Financial fields */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="vip_amount" className="control-label col-sm-3" style={{ textAlign: 'right' }}>VIP Amount</label>
                    <div className="col-sm-5">
                      <input id="vip_amount" name="vip_amount" value={client.vip_amount} onChange={handleChange} className="form-control" type="number" step="0.01" placeholder="Enter VIP amount" />
                    </div>
                  </div>

                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="savings_made" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Savings Made</label>
                    <div className="col-sm-5">
                      <input id="savings_made" name="savings_made" value={client.savings_made} onChange={handleChange} className="form-control" type="number" step="0.01" placeholder="Enter savings amount" />
                    </div>
                  </div>

                  {/* Internal Info */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 15 }}>
                    <label htmlFor="internal_info" className="control-label col-sm-3" style={{ textAlign: 'right', paddingTop: '8px' }}>Internal Info</label>
                    <div className="col-sm-5">
                      <textarea id="internal_info" name="internal_info" value={client.internal_info} onChange={handleChange} className="form-control" rows={16} />
                    </div>
                  </div>

                  {/* Region */}
                  <div className="form-group mb-3" style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
                    <label htmlFor="region" className="control-label col-sm-3" style={{ textAlign: 'right' }}>Region</label>
                    <div className="col-sm-5">
                      <select id="region" name="region" value={client.region} onChange={handleChange} className="form-control">
                        <option value="">Please Select</option>
                        {(dropdowns.regions || []).map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>

                </fieldset>

                {/* Information and Submit - matching Laravel create.blade.php */}
                <fieldset className="form-horizontal" style={{ marginTop: 30 }}>
                  <div className="form-group mb-3" style={{ flexDirection: 'column', alignContent: 'center', gap: 40 }}>
                    <div className="form-group mb-3" style={{ flexDirection: 'row', alignContent: 'center', gap: 40 }}>
                      <label className="control-label col-sm-3" style={{ textAlign: 'right' }}>Information</label>
                      <div className="col-sm-5">
                        <span className="help-block">After you update client you may add company contacts.</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignSelf: 'center', width: '100%', justifyContent: 'center' }}>
                      <button
                        type="submit"
                        className="btn"
                        style={{ 
                          minWidth: 120,
                          backgroundColor: '#17264b',
                          color: '#fff',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                        disabled={saving}
                      >
                        <i className="glyphicon glyphicon-ok"></i> {saving ? 'Saving...' : 'Save Client'}
                      </button>
                    </div>
                    {success && <span className="text-success ml-2">Saved!</span>}
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientEdit; 